import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { marketplaceAPI } from '../services/api';
import { Store, Search, Filter, Mail, Phone, Building2, Eye, MessageCircle, Share2, Copy, Check } from 'lucide-react';
import { getServerURL } from '../services/api';
import ImageCarousel from '../components/ImageCarousel';

const MarketplacePage = () => {
  const navigate = useNavigate();
  const [watches, setWatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    brand: '',
    min_price: '',
    max_price: '',
    condition: '',
    city: '',
    state: '',
    country: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0,
  });
  const [selectedWatch, setSelectedWatch] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    fetchMarketplaceWatches();
  }, [filters, pagination.page]);

  const fetchMarketplaceWatches = async () => {
    try {
      // Filter out empty values
      const cleanFilters = Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {});

      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
        ...cleanFilters,
      };
      const response = await marketplaceAPI.getAll(params);
      setWatches(response.data.watches);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        total_pages: response.data.total_pages,
      }));
    } catch (error) {
      console.error('Error fetching marketplace watches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleViewDetails = async (watchId) => {
    try {
      const response = await marketplaceAPI.getOne(watchId);
      setSelectedWatch(response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching watch details:', error);
    }
  };

  const handleMessageDealer = (dealer) => {
    navigate(`/messages?userId=${dealer.id}&userName=${encodeURIComponent(dealer.name)}&businessName=${encodeURIComponent(dealer.business_name || '')}`);
  };

  const handleCopyPublicLink = (watchId) => {
    const publicLink = `${window.location.origin}/watch/${watchId}`;
    navigator.clipboard.writeText(publicLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const getWatchImage = (watch) => {
    if (watch.images) {
      // Images can be array or JSON string
      let images = watch.images;
      if (typeof images === 'string') {
        try {
          images = JSON.parse(images);
        } catch (e) {
          console.error('[MarketplacePage] Error parsing images:', e);
          return null;
        }
      }
      if (Array.isArray(images) && images.length > 0) {
        const firstImage = images[0];
        // Check if it's already a full URL (Cloudinary)
        if (firstImage.startsWith('http')) {
          console.log('[MarketplacePage] Image URL (Cloudinary):', firstImage);
          return firstImage;
        }
        // Local file path
        const url = `${getServerURL()}/uploads/watches/${firstImage}`;
        console.log('[MarketplacePage] Image URL (local):', url);
        return url;
      }
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-900 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Store className="w-8 h-8 text-slate-900" />
            Dealer Marketplace
          </h1>
          <p className="text-gray-600 mt-1">Browse watches from other dealers</p>
        </div>
        <div className="text-sm text-gray-600">
          {pagination.total} watches available
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-3xl shadow-xl p-6 border-2 border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <h3 className="font-bold text-gray-900">Filters</h3>
        </div>
        <div className="space-y-4">
          {/* First Row - Search, Brand, Prices */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Search brand, model, reference..."
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                />
              </div>
            </div>

            {/* Brand */}
            <div>
              <input
                type="text"
                name="brand"
                value={filters.brand}
                onChange={handleFilterChange}
                placeholder="Brand"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
              />
            </div>

            {/* Price Range */}
            <div>
              <input
                type="number"
                name="min_price"
                value={filters.min_price}
                onChange={handleFilterChange}
                placeholder="Min Price"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
              />
            </div>
            <div>
              <input
                type="number"
                name="max_price"
                value={filters.max_price}
                onChange={handleFilterChange}
                placeholder="Max Price"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Second Row - Location Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <input
                type="text"
                name="city"
                value={filters.city}
                onChange={handleFilterChange}
                placeholder="City"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
              />
            </div>
            <div>
              <input
                type="text"
                name="state"
                value={filters.state}
                onChange={handleFilterChange}
                placeholder="State/Province"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
              />
            </div>
            <div>
              <input
                type="text"
                name="country"
                value={filters.country}
                onChange={handleFilterChange}
                placeholder="Country"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Watch Grid */}
      {watches.length === 0 ? (
        <div className="bg-white rounded-3xl shadow-xl p-12 text-center border-2 border-gray-100">
          <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No watches available</h3>
          <p className="text-gray-600">Check back later for new listings from other dealers</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {watches.map((watch) => (
            <div
              key={watch.id}
              className="bg-white rounded-3xl shadow-xl border-2 border-gray-100 overflow-hidden hover:shadow-2xl transition-all cursor-pointer group"
              onClick={() => handleViewDetails(watch.id)}
            >
              {/* Watch Image */}
              <div className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                <Store className="w-16 h-16 text-gray-300" />
                {getWatchImage(watch) && (
                  <img
                    src={getWatchImage(watch)}
                    alt={`${watch.brand} ${watch.model}`}
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                {/* Image Count Badge */}
                {Array.isArray(watch.images) && watch.images.length > 1 && (
                  <div className="absolute top-3 left-3 bg-black/70 text-white px-2 py-1 rounded-full text-xs font-bold">
                    {watch.images.length}
                  </div>
                )}
                <div className="absolute top-3 right-3 px-3 py-1 bg-white bg-opacity-90 rounded-full text-xs font-bold text-gray-700">
                  {watch.condition}
                </div>
              </div>

              {/* Watch Details */}
              <div className="p-5">
                <div className="mb-3">
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-slate-900 transition-colors">
                    {watch.brand}
                  </h3>
                  <p className="text-sm text-gray-600 line-clamp-1">{watch.model}</p>
                  {watch.reference && (
                    <p className="text-xs text-gray-500 mt-1">REF: {watch.reference}</p>
                  )}
                </div>

                <div className="text-2xl font-bold text-slate-900 mb-4">
                  ${(Number(watch.asking_price) || 0).toLocaleString()}
                </div>

                {/* Dealer Info */}
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <Building2 className="w-4 h-4" />
                    <span className="font-medium">{watch.dealer.business_name || watch.dealer.name}</span>
                  </div>
                </div>

                {/* View Details Button */}
                <button className="mt-4 w-full py-2 bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-xl font-semibold opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center gap-2">
                  <Eye className="w-4 h-4" />
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
            disabled={pagination.page === 1}
            className="px-4 py-2 bg-white border-2 border-gray-200 rounded-xl font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <div className="px-4 py-2 bg-slate-50 border-2 border-slate-200 rounded-xl font-bold text-slate-900">
            Page {pagination.page} of {pagination.total_pages}
          </div>
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.total_pages, prev.page + 1) }))}
            disabled={pagination.page === pagination.total_pages}
            className="px-4 py-2 bg-white border-2 border-gray-200 rounded-xl font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedWatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full my-8 border-2 border-gray-100">
            <div className="p-8">
              {/* Close Button */}
              <button
                onClick={() => setShowDetailModal(false)}
                className="float-right p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all"
              >
                ✕
              </button>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left: Watch Image */}
                <div>
                  <ImageCarousel
                    images={selectedWatch.images || []}
                    alt={`${selectedWatch.brand} ${selectedWatch.model}`}
                  />
                </div>

                {/* Right: Watch Details */}
                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-gray-500 mb-1">
                      {selectedWatch.condition} Condition
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                      {selectedWatch.brand}
                    </h2>
                    <p className="text-xl text-gray-700 mb-4">{selectedWatch.model}</p>
                    {selectedWatch.reference && (
                      <p className="text-sm text-gray-500">Reference: {selectedWatch.reference}</p>
                    )}
                  </div>

                  <div className="text-4xl font-bold text-slate-900 py-4 border-y border-gray-200">
                    ${(Number(selectedWatch.asking_price) || 0).toLocaleString()}
                  </div>

                  {/* Specifications */}
                  <div className="space-y-2 text-sm">
                    {selectedWatch.year && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Year:</span>
                        <span className="font-semibold text-gray-900">{selectedWatch.year}</span>
                      </div>
                    )}
                    {selectedWatch.case_size && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Case Size:</span>
                        <span className="font-semibold text-gray-900">{selectedWatch.case_size}</span>
                      </div>
                    )}
                    {selectedWatch.case_material && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Case Material:</span>
                        <span className="font-semibold text-gray-900">{selectedWatch.case_material}</span>
                      </div>
                    )}
                    {selectedWatch.movement && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Movement:</span>
                        <span className="font-semibold text-gray-900">{selectedWatch.movement}</span>
                      </div>
                    )}
                    {selectedWatch.box && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Box:</span>
                        <span className="font-semibold text-gray-900">{selectedWatch.box}</span>
                      </div>
                    )}
                    {selectedWatch.papers && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Papers:</span>
                        <span className="font-semibold text-gray-900">{selectedWatch.papers}</span>
                      </div>
                    )}
                  </div>

                  {selectedWatch.description && (
                    <div className="pt-4">
                      <h3 className="font-bold text-gray-900 mb-2">Description</h3>
                      <p className="text-gray-700 text-sm">{selectedWatch.description}</p>
                    </div>
                  )}

                  {/* Dealer Contact */}
                  <div className="pt-6 border-t border-gray-200">
                    <h3 className="font-bold text-gray-900 mb-4">Seller Information</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Building2 className="w-5 h-5 text-slate-900" />
                        <div>
                          <div className="font-bold text-gray-900">
                            {selectedWatch.dealer.business_name || selectedWatch.dealer.name}
                          </div>
                          <div className="text-sm text-gray-600">{selectedWatch.dealer.name}</div>
                        </div>
                      </div>
                      <a
                        href={`mailto:${selectedWatch.dealer.email}`}
                        className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl hover:bg-blue-100 transition-all"
                      >
                        <Mail className="w-5 h-5 text-blue-600" />
                        <span className="font-medium text-blue-600">{selectedWatch.dealer.email}</span>
                      </a>
                      {selectedWatch.dealer.phone && (
                        <a
                          href={`tel:${selectedWatch.dealer.phone}`}
                          className="flex items-center gap-3 p-3 bg-green-50 rounded-xl hover:bg-green-100 transition-all"
                        >
                          <Phone className="w-5 h-5 text-green-600" />
                          <span className="font-medium text-green-600">{selectedWatch.dealer.phone}</span>
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Share Public Link */}
                  <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-100">
                    <div className="flex items-start gap-3">
                      <Share2 className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-900 mb-1">
                          Shareable Public Link
                        </div>
                        <p className="text-xs text-gray-600 mb-3">
                          Anyone with this link can view this watch listing
                        </p>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={`${window.location.origin}/watch/${selectedWatch.id}`}
                            readOnly
                            className="flex-1 px-3 py-2 bg-white border border-blue-200 rounded-lg text-sm text-gray-700 font-mono"
                          />
                          <button
                            onClick={() => handleCopyPublicLink(selectedWatch.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                              linkCopied
                                ? 'bg-green-600 text-white'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                          >
                            {linkCopied ? (
                              <>
                                <Check className="w-4 h-4" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Copy className="w-4 h-4" />
                                Copy
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => handleMessageDealer(selectedWatch.dealer)}
                      className="flex-1 py-4 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-2xl font-bold shadow-lg hover:from-blue-700 hover:to-blue-600 transition-all flex items-center justify-center gap-2"
                    >
                      <MessageCircle className="w-5 h-5" />
                      Message Dealer
                    </button>
                    <button
                      onClick={() => window.location.href = `mailto:${selectedWatch.dealer.email}?subject=Inquiry about ${selectedWatch.brand} ${selectedWatch.model}`}
                      className="flex-1 py-4 bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-2xl font-bold shadow-lg hover:from-slate-950 hover:to-slate-800 transition-all flex items-center justify-center gap-2"
                    >
                      <Mail className="w-5 h-5" />
                      Email
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplacePage;
